import { Divider } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../../../assets/icons';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { useOnOutsideClick } from '../../../../hooks/useOnOutsideClick';
import { PaywallMetadata } from '../../../../hooks/usePaywallPrompt';
import { useTestPrepAccess } from '../../../../hooks/useTestPrepAccess';
import { IconWrapper } from '../../../_atoms/IconWrapper';

export const FloatingSelect = ({
  onSelect,
  options,
  value,
  placeholder,
  width,
  label,
  disabled,
  darkMode,
  style,
}: {
  value: string | undefined;
  options: {
    displayName: string;
    value: string;
    disabled?: boolean;
    icon?: JSX.Element;
    displayDivider?: boolean;
  }[];
  onSelect: (value: string) => void;
  placeholder?: string | React.ReactNode;
  width?: string;
  label?: string;
  darkMode?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string, disabled?: boolean) => {
    if (disabled) return;
    setShowDropdown(false);
    onSelect(value);
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef} $width={width}>
      {label && (
        <label
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: darkMode ? theme.colors.white : theme.colors.black,
          }}
        >
          {label}
        </label>
      )}
      <FakeInputV2
        $darkMode={darkMode}
        style={
          style
            ? { ...style, cursor: disabled ? 'not-allowed' : 'pointer' }
            : { cursor: disabled ? 'not-allowed' : 'pointer' }
        }
        onClick={() => {
          if (disabled) return;
          setShowDropdown(!showDropdown);
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedOption?.displayName || placeholder || 'select'}
          </span>
        </div>
        {!darkMode && (
          <IconWrapper height="12px" width="12px">
            <MenuArrowDownSvg />
          </IconWrapper>
        )}
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: darkMode ? '210px' : '100%',
                left: 0,
                background: theme.colors.white,
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <>
                  {option.displayDivider && (
                    <div style={{ height: 1, background: theme.colors.grey }} />
                  )}
                  <OptionContainer
                    $divider={option.displayDivider || false}
                    key={option.value}
                    $selected={option.value === value}
                    $disabled={option.disabled}
                    onClick={() => handleSelect(option.value, option.disabled)}
                  >
                    {option.icon && (
                      <IconWrapper style={{ marginRight: '8px' }}>
                        {option.icon}
                      </IconWrapper>
                    )}
                    {option.displayName}
                  </OptionContainer>
                </>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};
export const TestPrepFloatingSelect = ({
  onSelect,
  options,
  value,
  placeholder,
  width,
  label,
  disabled,
  darkMode,
  style,
  displayCantFindOption,
  handleShowRequestTestModal,
  promptPaywall,
  userAccessBadge,
  isReady,
  testRequest,
}: {
  value: string | undefined;
  options: {
    displayName: string;
    value: string;
    disabled?: boolean;
    icon?: JSX.Element;
    isEnterprise?: boolean;
    displayDivider?: boolean;
  }[];
  onSelect: (value: string) => void;
  placeholder?: string | React.ReactNode;
  width?: string;
  label: string;
  darkMode?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  displayCantFindOption?: boolean;
  handleShowRequestTestModal?: (location: string) => void;
  promptPaywall?: (metadata?: PaywallMetadata | undefined) => void;
  userAccessBadge?: UserAccessBadge | undefined;
  isReady?: boolean;
  testRequest?: React.ReactNode;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { hasTestPrepAccess } = useTestPrepAccess();

  const isFreemium =
    userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.freemium;

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (selectedValue: string, disabled?: boolean) => {
    if (disabled) return;
    if (selectedValue === value) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(false);
    onSelect(selectedValue);
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef} $width={width}>
      {label && (
        <label
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: darkMode ? theme.colors.white : theme.colors.black,
          }}
        >
          {label}
        </label>
      )}
      <FakeInputV2
        $darkMode={darkMode}
        style={
          style
            ? { cursor: disabled ? 'not-allowed' : 'pointer', ...style }
            : { cursor: disabled ? 'not-allowed' : 'pointer' }
        }
        onClick={() => {
          if (disabled) return;
          setShowDropdown(!showDropdown);
        }}
      >
        <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedOption?.displayName || placeholder || 'Select'}
          </span>
        </div>
        {!darkMode && (
          <IconWrapper height="12px" width="12px">
            <MenuArrowDownSvg />
          </IconWrapper>
        )}
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: darkMode ? '210px' : '100%',
                left: 0,
                background: theme.colors.white,
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map((option, i) => (
                <>
                  {option.displayDivider && (
                    <div key={i} style={{ height: 1, background: theme.colors.grey }} />
                  )}
                  <OptionContainer
                    $divider={option.displayDivider || false}
                    key={option.value}
                    $selected={option.value === value}
                    $disabled={option.disabled}
                    onClick={() => {
                      handleSelect(option.value, option.disabled);
                    }}
                  >
                    {option.isEnterprise && (
                      <IconWrapper style={{ marginRight: '8px', flexShrink: 0 }}>
                        <EnterprisePlanIcon
                          width={15}
                          height={15}
                          color={
                            hasTestPrepAccess ? theme.colors.black : theme.colors.yellow
                          }
                        />
                      </IconWrapper>
                    )}
                    {option.displayName}
                    {!option.isEnterprise && isFreemium && <FreeBadge>Free</FreeBadge>}
                  </OptionContainer>
                </>
              ))}
              {displayCantFindOption && handleShowRequestTestModal && (
                <>
                  <Divider
                    style={{
                      minWidth: '100%',
                      width: '100%',
                      margin: '0px 0px 10px 0px',
                    }}
                  />
                  <div
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: '10px 15px',
                    }}
                    onClick={() => {
                      handleShowRequestTestModal(label);
                    }}
                  >
                    {testRequest}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

const OptionContainer = styled.div<{
  $selected?: boolean;
  $disabled?: boolean;
  $divider: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 100%;
  ${({ $divider }) =>
    $divider &&
    css`
      background: #fafafa;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      color: ${theme.colors.grey};
    `}

  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'none' : theme.colors.greyLight)};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
`;

const FakeInputV2 = styled.div<{ $darkMode?: boolean }>`
  width: 100%;
  min-width: ${({ $darkMode }) => ($darkMode ? 20 : 70)}px;
  display: ${({ $darkMode }) => ($darkMode ? 'flex' : 'grid')};
  justify-content: space-between;
  grid-template-columns: auto 15px;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  gap: 5px;

  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  background: ${({ $darkMode }) => ($darkMode ? 'none' : theme.colors.white)};
  color: #5b5b5a;
`;

const Wrapper = styled.div<{ $width?: string }>`
  cursor: pointer;
  position: relative;
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
    `}
`;

const FreeBadge = styled.span`
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 10px;
  background: ${theme.colors.orange};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
`;
